import { makeObservable, computed } from 'mobx';
import { ApiQueryName } from '../../../api/apiSchema';
import { BindingProps, StoreNode } from '../../../store';
import { Store } from '../../../store/store';
import { ChartPageParams } from '../chartSchema';
import { MetricCounterDataSource } from './metricCounterDataSource';
import { Error } from '../../../core/error';

type ValueAccessor = (apiResult: unknown) => [number | null, (Error | string)?];

type Props = BindingProps<{
  label?: string;
  queryName?: ApiQueryName;
  queryParams?: ChartPageParams;
  display?: boolean;
  valueAccessor?: ValueAccessor;
  outputData?: (result: number) => string | null;
}>

export class MetricCounterState
  extends StoreNode {

  readonly nodeType = 'MetricCounterState';

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      label: computed,
      queryName: computed,
      queryParams: computed,
      valueAccessor: computed,
      display: computed,
      outputData: computed,
    });
  }

  // #region Props
  get label(): string | null {
    return this.getResolvedProp('label') ?? null;
  }

  get queryName(): ApiQueryName | null {
    return this.getResolvedProp('queryName') ?? null;
  }

  get queryParams(): ChartPageParams | null {
    return this.getResolvedProp('queryParams') ?? null;
  }

  get valueAccessor(): ValueAccessor | null {
    return this.getProp('valueAccessor') ?? null;
  }

  get display(): boolean {
    return this.getResolvedProp('display') ?? true;
  }
  // #endregion

  get outputData(): string | number | null {
    const outputDataCallback: ((result: number) => string | null) | null = this.getProp('outputData') ?? null;

    return (outputDataCallback && outputDataCallback(this.dataSource.value)) ||
      this.dataSource.value;
  }

  readonly dataSource = new MetricCounterDataSource(this.store, {
    queryName: () => this.queryName,
    queryParams: () => this.queryParams,
    valueAccessor: this.valueAccessor
  });

  async fetch() {
    return this.dataSource.fetch();
  }
  abort() {
    this.dataSource.cancel();
  }
}
